import type { Event, StackFrame } from '@sentry/vue'
import { CODIGOS_TECNICOS_ERRO, NATUREZAS_FALHA, OPERACOES_ERRO, ORIGENS_ERRO } from '@next/compartilhados/observabilidade'
import type { ContextoErro } from '@next/compartilhados/observabilidade'

const IDENTIFICADOR_DEBUG = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
const LIMITE_IMAGENS_DEBUG = 40
const inteiroPositivo = (valor: unknown): valor is number => (
  typeof valor === 'number' && Number.isSafeInteger(valor) && valor >= 0
)

/** Preserva o diagnóstico; remove credenciais reconhecíveis, não nomes ou mensagens de domínio. */
const sanitizarTexto = (valor: unknown, limite = 4096): string | undefined => {
  if (typeof valor !== 'string' || !valor.trim()) return undefined
  return valor
    .replace(/https?:\/\/[^\s<>"'`]+/gi, endereco => {
      try {
        const url = new URL(endereco)
        url.username = ''; url.password = ''; url.search = ''; url.hash = ''
        return url.toString()
      } catch { return '[URL inválida]' }
    })
    .replace(/\b(Bearer|Basic)\s+[a-z0-9._~+/=-]+/gi, '$1 [REMOVIDO]')
    .replace(/((?:["']?)(?:authorization|cookie|set-cookie|password|senha|access[_-]?token|refresh[_-]?token|id[_-]?token|auth[_-]?token|token|api[_-]?key|client[_-]?secret)(?:["']?)\s*[:=]\s*)(\[REMOVIDO\]|"[^"\n]*"|'[^'\n]*'|[^\s,;}\]]+)/gi, '$1[REMOVIDO]')
    .replace(/\beyJ[a-z0-9_-]+\.[a-z0-9_-]+\.[a-z0-9_-]+\b/gi, '[REMOVIDO]')
    .slice(0, limite)
}

/** Converte os campos do SDK para o contrato da aplicação. Campos livres são ignorados. */
export const contextoDoEvento = (evento: Event): ContextoErro => {
  const natureza = NATUREZAS_FALHA.find(valor => valor === evento.tags?.natureza_falha)
  const codigo = CODIGOS_TECNICOS_ERRO.find(valor => valor === evento.tags?.codigo_tecnico)
  const resposta = evento.contexts?.resposta_http
  const origemStack = evento.tags?.origem_stack
  return {
    origem: ORIGENS_ERRO.find(valor => valor === evento.tags?.origem) ?? 'javascript',
    operacao: OPERACOES_ERRO.find(valor => valor === evento.tags?.operacao) ?? 'captura',
    ...(typeof evento.tags?.status_http === 'number' ? { statusHttp: evento.tags.status_http } : {}),
    ...(natureza ? { naturezaFalha: natureza } : {}),
    ...(codigo ? { codigoTecnico: codigo } : {}),
    ...(typeof evento.tags?.metodo_http === 'string' ? { metodoHttp: evento.tags.metodo_http } : {}),
    ...(typeof evento.tags?.servico_http === 'string' ? { servicoHttp: evento.tags.servico_http } : {}),
    ...(typeof evento.tags?.servidor_http === 'string' ? { servidorHttp: evento.tags.servidor_http } : {}),
    ...(typeof evento.tags?.endpoint === 'string' ? { endpoint: evento.tags.endpoint } : {}),
    ...(typeof evento.tags?.endpoint_modelo === 'string' ? { endpointModelo: evento.tags.endpoint_modelo } : {}),
    ...(typeof resposta?.mensagem === 'string' ? { mensagemApi: resposta.mensagem } : {}),
    ...(typeof resposta?.codigo === 'string' ? { codigoApi: resposta.codigo } : {}),
    ...(typeof resposta?.request_id === 'string' ? { requestId: resposta.request_id } : {}),
    ...(origemStack === 'original' || origemStack === 'causa' || origemStack === 'captura' ? { origemStack } : {}),
  }
}

/** Só o caminho: parâmetros de busca, fragmento e credenciais da URL ficam fora do evento. */
const sanitizarEndpoint = (endereco: unknown): string | undefined => {
  if (typeof endereco !== 'string' || !endereco.trim()) return undefined
  try {
    const url = new URL(endereco, 'https://observabilidade.invalid/')
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined
    return sanitizarTexto(url.pathname, 2048)
  } catch { return undefined }
}

/** Somente caminhos de código. URLs de navegação, query, hash e credenciais não saem. */
export const sanitizarArquivo = (arquivo: string | undefined, origemAplicacao: string): string | undefined => {
  if (!arquivo || arquivo.length > 500) return undefined
  try {
    const url = new URL(arquivo, origemAplicacao)
    if (url.origin !== new URL(origemAplicacao).origin || url.username || url.password) return undefined
    if (!/^\/(?:static\/)?(?:js|assets)\/[a-zA-Z0-9_.-]{1,150}\.js$/.test(url.pathname)) return undefined
    return `${url.origin}${url.pathname}`
  } catch {
    return undefined
  }
}

const sanitizarFrame = (frame: StackFrame, origemAplicacao: string): StackFrame => {
  const filename = sanitizarArquivo(frame.filename, origemAplicacao)
  const funcao = sanitizarTexto(frame.function, 300)
  return {
    ...(filename ? { filename } : {}),
    ...(inteiroPositivo(frame.lineno) ? { lineno: frame.lineno } : {}),
    ...(inteiroPositivo(frame.colno) ? { colno: frame.colno } : {}),
    ...(funcao ? { function: funcao } : {}),
    ...(typeof frame.debug_id === 'string' && IDENTIFICADOR_DEBUG.test(frame.debug_id) ? { debug_id: frame.debug_id } : {}),
  }
}

const sanitizarDebugMeta = (evento: Event, origemAplicacao: string): Event['debug_meta'] | undefined => {
  const imagens: NonNullable<NonNullable<Event['debug_meta']>['images']> = []
  for (const imagem of (evento.debug_meta?.images ?? []).slice(-LIMITE_IMAGENS_DEBUG)) {
    if (imagem.type !== 'sourcemap' || typeof imagem.debug_id !== 'string' || typeof imagem.code_file !== 'string'
      || !IDENTIFICADOR_DEBUG.test(imagem.debug_id)) continue
    const arquivo = sanitizarArquivo(imagem.code_file, origemAplicacao)
    if (arquivo) imagens.push({ type: 'sourcemap', code_file: arquivo, debug_id: imagem.debug_id })
  }
  return imagens.length > 0 ? { images: imagens } : undefined
}

/** Mantém mensagem, tipo, stack e identidade; não serializa objetos brutos da aplicação. */
export const sanitizarEvento = (evento: Event, contexto: ContextoErro, origemAplicacao: string): (Event & { type: undefined }) | null => {
  try {
    const origem = ORIGENS_ERRO.find(valor => valor === contexto.origem) ?? 'manual'
    const operacao = OPERACOES_ERRO.find(valor => valor === contexto.operacao) ?? 'captura'
    const natureza = NATUREZAS_FALHA.find(valor => valor === contexto.naturezaFalha)
    const codigo = CODIGOS_TECNICOS_ERRO.find(valor => valor === contexto.codigoTecnico)
    const metodo = typeof contexto.metodoHttp === 'string' && /^[a-zA-Z]{1,20}$/.test(contexto.metodoHttp)
      ? contexto.metodoHttp.toUpperCase() : undefined
    const servico = sanitizarTexto(contexto.servicoHttp, 100)
    const servidor = sanitizarTexto(contexto.servidorHttp, 255)
    const endpoint = sanitizarEndpoint(contexto.endpoint)
    const endpointModelo = sanitizarEndpoint(contexto.endpointModelo)
    const mensagemApi = sanitizarTexto(contexto.mensagemApi)
    const codigoApi = sanitizarTexto(contexto.codigoApi, 200)
    const requestId = sanitizarTexto(contexto.requestId, 200)
    const resposta = {
      ...(mensagemApi ? { mensagem: mensagemApi } : {}),
      ...(codigoApi ? { codigo: codigoApi } : {}),
      ...(requestId ? { request_id: requestId } : {}),
    }
    const origemStack = contexto.origemStack
    const status = inteiroPositivo(contexto.statusHttp) && contexto.statusHttp >= 100 && contexto.statusHttp <= 599 ? contexto.statusHttp : undefined
    const mensagem = sanitizarTexto(evento.message)
    const usuarioId = sanitizarTexto(evento.user?.id, 200)
    const usuarioEmail = sanitizarTexto(evento.user?.email, 320)
    const empresaId = usuarioId ? sanitizarTexto(evento.tags?.empresa_id, 200) : undefined
    const empresaNome = empresaId ? sanitizarTexto(evento.tags?.empresa_nome, 300) : undefined
    const debugMeta = sanitizarDebugMeta(evento, origemAplicacao)
    const seguro: Event & { type: undefined } = {
      type: undefined,
      level: 'error',
      platform: 'javascript',
      tags: {
        origem,
        operacao,
        ...(status !== undefined ? { status_http: status } : {}),
        ...(natureza ? { natureza_falha: natureza } : {}),
        ...(codigo ? { codigo_tecnico: codigo } : {}),
        ...(metodo ? { metodo_http: metodo } : {}),
        ...(servico ? { servico_http: servico } : {}),
        ...(servidor ? { servidor_http: servidor } : {}),
        ...(endpoint ? { endpoint } : {}),
        ...(endpointModelo ? { endpoint_modelo: endpointModelo } : {}),
        ...(origemStack === 'original' || origemStack === 'causa' || origemStack === 'captura' ? { origem_stack: origemStack } : {}),
        ...(empresaId ? { empresa_id: empresaId } : {}),
        ...(empresaNome ? { empresa_nome: empresaNome } : {}),
      },
      ...(usuarioId ? { user: {
        id: usuarioId,
        ...(usuarioEmail ? { email: usuarioEmail } : {}),
      } } : {}),
      ...(mensagem ? { message: mensagem } : {}),
      exception: {
        values: (evento.exception?.values ?? [{}]).slice(-3).map(excecao => ({
          type: sanitizarTexto(excecao.type, 200) ?? 'Error',
          value: sanitizarTexto(excecao.value) ?? mensagem ?? 'Erro sem mensagem',
          stacktrace: { frames: (excecao.stacktrace?.frames ?? []).slice(-40).map(frame => sanitizarFrame(frame, origemAplicacao)) },
        })),
      },
      ...(debugMeta ? { debug_meta: debugMeta } : {}),
      contexts: {
        service: { name: 'gestao-web' },
        ...(Object.keys(resposta).length ? { resposta_http: resposta } : {}),
      },
    }
    if (typeof evento.event_id === 'string' && /^[a-f0-9]{32}$/i.test(evento.event_id)) seguro.event_id = evento.event_id
    if (typeof evento.timestamp === 'number' && Number.isFinite(evento.timestamp)) seguro.timestamp = evento.timestamp
    // Contextos explícitos e limitados; sem resposta bruta, breadcrumbs, request, extra ou anexos.
    return new TextEncoder().encode(JSON.stringify(seguro)).byteLength <= 65536 ? seguro : null
  } catch {
    return null
  }
}

/** Fallback limitado para deduplicação; o endpoint enviado continua intacto. */
const normalizarEndpointDaAssinatura = (endpoint: unknown): string | undefined => {
  if (typeof endpoint !== 'string') return undefined
  return endpoint.split('/').map(segmento => (
    /^(?:\d+|[a-f0-9]{24}|[a-f0-9]{8}-(?:[a-f0-9]{4}-){3}[a-f0-9]{12})$/i.test(segmento) ? ':id' : segmento
  )).join('/')
}

/** Recebe o evento já sanitizado. Origem da captura e IDs não identificam a causa. */
export const assinaturaEvento = (evento: Event): string => JSON.stringify({
  excecoes: evento.exception?.values?.map(excecao => ({
    tipo: excecao.type,
    mensagem: excecao.value,
    frames: excecao.stacktrace?.frames?.map(frame => ({ arquivo: frame.filename, linha: frame.lineno, coluna: frame.colno })),
  })),
  operacao: evento.tags?.operacao,
  natureza: evento.tags?.natureza_falha,
  status: evento.tags?.status_http,
  codigo: evento.tags?.codigo_tecnico,
  metodo: evento.tags?.metodo_http,
  servico: evento.tags?.servico_http,
  servidor: evento.tags?.servidor_http,
  endpoint: evento.tags?.endpoint_modelo ?? normalizarEndpointDaAssinatura(evento.tags?.endpoint),
  codigoApi: evento.contexts?.resposta_http?.codigo,
  mensagemApi: evento.contexts?.resposta_http?.mensagem,
  // Request ID localiza uma requisição; não identifica uma causa diferente.
})
